//================================================================
//  Component: Edit PO Line Item Pane
//================================================================

//  Purpose: This pane will allow users to edit a PO line item in an agreement

//  Properties:
//    - agreement = {An object, contains the inventary agreement details}
//    - editPOLineItemPaneOpen = {useState, used to determine if the pane is visible}
//    - setEditPOLineItemPaneOpen = {useState, used to toggle the visibility of the edit po line pane}
//    - polineitemIndex = {The index of the polineitem in the agreement.polineitems array}

//  Example:
//    <EditPOLineItemPane
//      agreement={agreement}
//      editPOLineItemPaneOpen={editPOLineItemPaneOpen}
//      setEditPOLineItemPaneOpen={setEditPOLineItemPaneOpen}
//      polineitemIndex={polineitemIndex}
//    ></EditPOLineItemPane>    

//================================================================


//Libraries
import React, { useState, useEffect, useContext } from 'react';
import { useForm } from 'react-hook-form';

//Contexts
import { GetUser } from '../../../../../Library/GlobalContexts';

//Components
import FiscalYearPicker from './FiscalYearPicker';
import PTELookupRHF from '../../../../../Components/PTELookupRHF/PTELookupRHF';

//Functions
import WriteDocument from '../../../../../Library/WriteDocument';

//Images
import Error from '../../../../../Components/Images/Icon_ErrorFatal_Red.svg';
import Loading from '../../../../../Components/Images/Image_Loading_Ripple.svg';

export default function EditPOLineItemPane({
  agreement,
  editPOLineItemPaneOpen,
  setEditPOLineItemPaneOpen,
  polineitemIndex,
}) {

  //------------------------------------------------------
  //  useRef & useContexts
  //------------------------------------------------------

  const getUser = useContext(GetUser);

  //------------------------------------------------------
  //  useStates
  //------------------------------------------------------

  // Used to set pane status > 'onload', 'pending', 'error'
  const [paneStatus, setPaneStatus] = useState('onload');

  // --------------------------------------------------
  //  Form State
  // --------------------------------------------------

  const { register, handleSubmit, setValue, formState: { errors }, reset, watch, clearErrors } = useForm();

  //------------------------------------------------------
  //  Functions
  //------------------------------------------------------

  // Submit Handler
  function onSubmit(data) {

    // ----------------------------------------------
    //  Submit Request
    // ----------------------------------------------

    const updatedDocument = {
      'projectcode': data.projectcode,
      'tasknumber': data.tasknumber,
      'expendituretype': data.expendituretype,
      'currency': data.currency,
      'poamount': data.poamount,
      'taxrate': data.taxrate,
      'fiscalyear': `FY${data.fiscalyear}`,
      'description': data.description
    }

    // Initialize polineitems if it's undefined or null
    agreement.polineitems = agreement.polineitems || [];

    // update the polineitems array of polineitemIndex index
    agreement.polineitems[polineitemIndex] = updatedDocument;

    // Add audit logging
    agreement.lastmodifieddate = new Date();
    agreement.lastmodifiedby = {
      'email': getUser.emailaddress,
      'givenname': getUser.givenname,
      'surname': getUser.surname,
    };

    setPaneStatus('pending');

    WriteDocument('agreements', agreement.agreementid, agreement, true)
    .then(() => {

      reset();
      clearErrors();

      setPaneStatus('onload');
      setEditPOLineItemPaneOpen(false);

    })
    .catch(() => {

      console.log('Error');
      setPaneStatus('error');

    })

  }

  //------------------------------------------------------
  //  useEffect
  //------------------------------------------------------

  // Set all the default values on the form
  useEffect(() => {

    if (agreement === undefined) return;

    const polineitem = agreement?.polineitems[polineitemIndex];

    setValue('projectcode', polineitem?.projectcode || '');
    setValue('tasknumber', polineitem?.tasknumber || '');
    setValue('expendituretype', polineitem?.expendituretype || '');
    setValue('currency', polineitem?.currency || '');
    setValue('poamount', polineitem?.poamount || '');
    setValue('taxrate', polineitem?.taxrate || '');
    setValue('fiscalyear', polineitem?.fiscalyear?.replace(/^FY/, '') || '');
    setValue('description', polineitem?.description || '');

    // eslint-disable-next-line
  }, [agreement, editPOLineItemPaneOpen])

  //------------------------------------------------------
  //  HTML
  //------------------------------------------------------

  if (!editPOLineItemPaneOpen) return null;

  // ---------------------------------------------------
  //  Onload
  // ---------------------------------------------------

  if (paneStatus === 'onload') {
    return (
      <div className='Pane-Background'>
        <dialog className='Pane-Container' style={{ padding: '0px' }}>

          <form className='grid grid-rows-[70px_1fr_70px] items-center justify-between w-full overflow-hidden' onSubmit={handleSubmit(onSubmit)}>

            {/* ===================================== */}
            {/*  Header                               */}
            {/* ===================================== */}

            <div className='flex flex-col gap-2 p-4'>
              <h4 className='text-[22px] p-0 m-0'> Edit PO Line Item </h4>
            </div>

            {/* ===================================== */}
            {/*  Form                                 */}
            {/* ===================================== */}

            <div className='w-[50vw] h-full flex flex-col overflow-y-auto p-4 px-4 py-2 border-solid border shadow-inner'>

              {/* ===================================== */}
              {/*  Recharge Information                 */}
              {/* ===================================== */}

              <div className='FormComponent-Row'>
                <label className='font-medium'> Recharge Information <span className='text-[#C4314B]'>*</span> </label>

                <i className='mb-3'>Project code, task number and expenditure type allow the recharge of technology costs.</i>

                <PTELookupRHF
                  required={true}
                  register={register}
                  setValue={setValue}
                  errors={errors}
                  clearErrors={clearErrors}
                  disabled={false}
                  watch={watch}
                ></PTELookupRHF>

              </div>

              {/* ===================================== */}
              {/*  Currency                             */}
              {/* ===================================== */}

              <div className='FormComponent-Row'>
                <label className='font-medium'>Currency <span className='text-[#C4314B]'>*</span></label>
                <select
                  className={errors.currency ? ('Input-Field-Select-Error') : ('Input-Field-Select')}
                  {...register('currency', {
                    required: 'This field is required'
                  })}
                  style={{ width: '300px' }}
                >
                  <option hidden value=''>-</option>
                  <option value='AUD'> AUD </option>
                  <option value='SGD'> SGD </option>
                  <option value='GBP'> GBP </option>
                  <option value='USD'> USD </option>
                  <option value='EUR'> EUR </option>
                  <option value='MYR'> MYR </option>
                </select>

                {errors.currency && <p className='font-medium text-sm text-[#DE0000] my-2'>{errors.currency?.message}</p>}
              </div>

              {/* ===================================== */}
              {/*  PO Amount (exTax)                    */}
              {/* ===================================== */}

              <div className='FormComponent-Row'>
                <label className='font-medium'>PO Amount (exTax)  <span className='text-[#C4314B]'>*</span></label>
                <input
                  className={errors.poamount ? ('Input-Field-Select-Error') : ('Input-Field-Select')}
                  style={{ width: '300px' }}
                  type='number'
                  step='0.01'
                  {...register('poamount', {
                    required: 'This field is required',
                    pattern: {
                      value: /^\d+(\.\d{1,2})?$/,
                      message: 'Only numbers allowed (up to 2 decimal places)',
                    },
                  })}
                  placeholder={'100,000'}
                ></input>

                {errors.poamount && <p className='font-medium text-sm text-[#DE0000] my-2'>{errors.poamount?.message}</p>}
              </div>

              {/* ===================================== */}
              {/*  Tax rate (%)                         */}
              {/* ===================================== */}

              <div className='FormComponent-Row'>
                <label className='font-medium'>Tax rate (%)<span className='text-[#C4314B]'>*</span></label>
                <input
                  className={errors.taxrate ? ('Input-Field-Select-Error') : ('Input-Field-Select')}
                  style={{ width: '300px' }}
                  type='number'
                  step='0.01'
                  {...register('taxrate', {
                    required: 'This field is required',
                    pattern: {
                      value: /^\d+(\.\d{1,2})?$/,
                      message: 'Only numbers allowed (up to 2 decimal places)',
                    },
                    min: {
                      value: 0,
                      message: 'Value must be at least 0',
                    },
                    max: {
                      value: 100,
                      message: 'Value must not exceed 100',
                    },
                  })}
                  placeholder={'10%'}
                ></input>

                {errors.taxrate && <p className='font-medium text-sm text-[#DE0000] my-2'>{errors.taxrate?.message}</p>}
              </div>

              {/* ===================================== */}
              {/*  Fiscal Year                          */}
              {/* ===================================== */}

              <FiscalYearPicker register={register} errors={errors}></FiscalYearPicker>

              {/* ===================================== */}
              {/*  Description                          */}
              {/* ===================================== */}

              <div className='FormComponent-Row'>
                <label className='font-medium'>Description</label>
                <textarea
                  className='Input-Field-TextArea'
                  style={{ height: '110px' }}
                  type='text'
                  placeholder='Add a description...'
                  {...register('description')}
                ></textarea>
              </div>

            </div>

            {/* ------------------------------- */}
            {/*  Submit Buttons                 */}
            {/* ------------------------------- */}

            <div className='flex flex-row gap-2 px-4 py-3'>
              <button
                className='Primary-Button'
                type='submit'
                value='submit'
                disabled={errors.projectcode || errors.tasknumber || errors.expendituretype || errors.currency || errors.poamount || errors.taxrate || errors.fiscalyear}
              >
                Save
              </button>
              <button className='Secondary-Button' onClick={() => {

                reset();
                clearErrors();

                setEditPOLineItemPaneOpen(false)
              }}>
                Cancel
              </button>
            </div>
          </form>
        </dialog>
      </div>
    )
  }

  // ---------------------------------------------------
  //  Pending
  // ---------------------------------------------------

  else if (paneStatus === 'pending') {
    return (
      <div className='Pane-Background'>
        <dialog className='Pane-Container flex justify-center items-center text-center' >
          <img alt='loading-circle-icon' src={Loading}></img>
        </dialog>
      </div>
    )
  }

  // ---------------------------------------------------
  //  Error
  // ---------------------------------------------------

  else if (paneStatus === 'error') {
    return (
      <div className='Pane-Background'>

        {/* Pane Container */}
        <dialog className='Pane-Container'>
          <div className='flex flex-col gap-2 w-full justify-center items-center'>

            <img className='my-2 w-[100px]' src={Error} alt='Error'></img>
            <h4 className='mb-0'>Oops! Something went wrong.</h4>
            <p className='text-center leading-[1.7]'>
              An error occurred while we processed your request.
            </p>
            <button
              className='Primary-Button'
              onClick={() => {

                reset();
                clearErrors();

                setPaneStatus('onload');
                setEditPOLineItemPaneOpen(false);
              }}
            >
              Close
            </button>
          </div>
        </dialog>
      </div>

    )
  }

  //------------------------------------------------------
}
